import { Button, Calendar, Tag, Typography } from "antd";
import { audioMessage as message } from "@components/Common/Notifications/audioMessage";
import { useEffect, useRef, useState } from "react";
import { DATE_FORMAT } from "../../Utils/date_utils";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import {
  getCalendarDayTags,
  bulkUpdateCalendarDayTags,
} from "../../store/actions/team.actions";
import { CalendarTagsList } from "./CalendarTagsList";
import { CreateTagDialog } from "./CreateTagDialog";
import { TagAvatar } from "../Avatars/TagAvatar";
import { TagBadge } from "../Overview/CalendarView/TagBadge";
import { PermissionOverlay, PermissionedButton, useUserPermissions } from "../PermissionedComponents";
import "../../Styles/styles-RestTravelDays.css";

export const RestDays = () => {
  const dispatch = useDispatch();
  const { calendarTags, calendarDayTags } = useSelector(({ team }) => team);
  const canManage = useUserPermissions()?.ManageCalendarTags;

  const [panel, setPanel] = useState(dayjs());
  const panelChange = useRef(false);

  // paint-mode state
  const [activeTagIds, setActiveTagIds] = useState(new Set());
  // staged: { [tagId]: { assign: Set<dateString>, remove: Set<dateString> } }
  const [staged, setStaged] = useState({});
  const [saving, setSaving] = useState(false);

  // dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTag, setEditTag] = useState(null);

  const tags = Object.values(calendarTags);

  // fetch day-tags for the visible month (+/- 1 month buffer)
  useEffect(() => {
    const start = panel.startOf("month").subtract(1, "month").format(DATE_FORMAT);
    const end = panel.endOf("month").add(1, "month").format(DATE_FORMAT);
    dispatch(getCalendarDayTags(start, end));
  }, [panel]);

  const handleSelectTag = (tagId) => {
    setActiveTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  const handleDateClick = (date) => {
    if (!canManage || activeTagIds.size === 0) return;
    if (panelChange.current) {
      panelChange.current = false;
      return;
    }
    const ds = date.format(DATE_FORMAT);

    setStaged((prev) => {
      const next = { ...prev };
      for (const tagId of activeTagIds) {
        const tagStaged = next[tagId] ?? { assign: new Set(), remove: new Set() };
        const existsInStore = (calendarDayTags[ds] ?? []).some((e) => e.tag_id === tagId);
        const alreadyAssigned = existsInStore && !tagStaged.remove.has(ds);
        const stagedAssign = tagStaged.assign.has(ds);

        const nextTag = { assign: new Set(tagStaged.assign), remove: new Set(tagStaged.remove) };
        if (alreadyAssigned) {
          nextTag.remove.add(ds);
          nextTag.assign.delete(ds);
        } else if (stagedAssign) {
          nextTag.assign.delete(ds);
        } else {
          nextTag.assign.add(ds);
          nextTag.remove.delete(ds);
        }
        next[tagId] = nextTag;
      }
      return next;
    });
  };

  const handleSave = async () => {
    const tagsToSave = Object.entries(staged).filter(
      ([, s]) => s.assign.size > 0 || s.remove.size > 0
    );
    if (tagsToSave.length === 0) return;
    setSaving(true);
    try {
      const start = panel.startOf("month").subtract(1, "month").format(DATE_FORMAT);
      const end = panel.endOf("month").add(1, "month").format(DATE_FORMAT);
      await Promise.all(
        tagsToSave.map(([tagId, s]) =>
          dispatch(bulkUpdateCalendarDayTags(tagId, [...s.assign], [...s.remove]))
        )
      );
      message.success("Calendar updated");
      await dispatch(getCalendarDayTags(start, end));
      setStaged({});
    } finally {
      setSaving(false);
    }
  };

  const dateCellRender = (value) => {
    const ds = value.format(DATE_FORMAT);
    const isToday = ds === dayjs().format(DATE_FORMAT);

    // determine if any active tag is on this date (live + staged)
    let activeOn = false;
    let activeColor = "#1890ff";
    if (activeTagIds.size > 0) {
      for (const tagId of activeTagIds) {
        const tagStaged = staged[tagId] ?? { assign: new Set(), remove: new Set() };
        const existsInStore = (calendarDayTags[ds] ?? []).some((e) => e.tag_id === tagId);
        if ((existsInStore && !tagStaged.remove.has(ds)) || tagStaged.assign.has(ds)) {
          activeOn = true;
          activeColor = calendarTags[tagId]?.color ?? "#1890ff";
          break;
        }
      }
    }

    const stagedAssignTagIds = Object.entries(staged)
      .filter(([, s]) => s.assign.has(ds))
      .map(([tagId]) => tagId);
    const stagedRemoveTagIds = Object.entries(staged)
      .filter(([, s]) => s.remove.has(ds))
      .map(([tagId]) => tagId);

    return (
      <div style={{ padding: "0px 4px" }}>
        <div
          style={{
            height: 64,
            display: "flex",
            flexDirection: "column",
            gap: 4,
            borderTop: activeOn ? `2px solid ${activeColor}` : "2px solid #d9d9d9",
            backgroundColor: isToday ? "#e6f4ff" : "#fff",
            cursor: canManage && activeTagIds.size > 0 ? "crosshair" : "default",
          }}
        >
          {value.get("date")}
          <div style={{ position: "relative", flex: 1 }}>
            <TagBadge
              dateString={ds}
              size={16}
              stagedAssignTagIds={stagedAssignTagIds}
              stagedRemoveTagIds={stagedRemoveTagIds}
            />
          </div>
        </div>
      </div>
    );
  };

  const hasStagedChanges = Object.values(staged).some((s) => s.assign.size > 0 || s.remove.size > 0);

  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 24, padding: "0px 0px 24px" }}>
      <CalendarTagsList
        onEdit={(tag) => { setEditTag(tag); setDialogOpen(true); }}
        onCreateClick={() => { setEditTag(null); setDialogOpen(true); }}
      />

      <div>
        <div>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            Select one or more tags, then click dates to toggle them.
          </Typography.Paragraph>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {tags.map((tag) => {
              const isActive = activeTagIds.has(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => handleSelectTag(tag.id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 4,
                    borderRadius: "50%",
                    border: isActive ? `2px solid ${tag.color}` : "1px solid #d9d9d9",
                    background: isActive ? `${tag.color}22` : "#fff",
                    cursor: "pointer",
                  }}
                >
                  <TagAvatar tag={tag} size={18} />
                </button>
              );
            })}
          </div>
        </div>

        <PermissionOverlay
          permissionCheck="ManageCalendarTags"
          tooltipMessage="You don't have permission to manage calendar tags"
          style={{ marginTop: 12 }}
        >
          <Calendar
            mode="month"
            style={{ width: 660, borderBottom: "1px solid #f0f0f0" }}
            fullCellRender={dateCellRender}
            onSelect={handleDateClick}
            onPanelChange={(v) => { panelChange.current = true; setPanel(v); }}
            fullscreen={false}
          />
        </PermissionOverlay>

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 16 }}>
          <PermissionedButton
            type="primary"
            permissionCheck="ManageCalendarTags"
            onClick={handleSave}
            disabled={!hasStagedChanges}
            loading={saving}
          >
            Save Changes
          </PermissionedButton>
          {hasStagedChanges && (
            <Button onClick={() => setStaged({})}>
              Discard
            </Button>
          )}
        </div>
      </div>

      <CreateTagDialog
        open={dialogOpen}
        editTag={editTag}
        onClose={() => { setDialogOpen(false); setEditTag(null); }}
      />
    </div>
  );
};
